import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useYouTube } from "@/context/YouTubeContext";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { VideoCard } from "@/components/VideoCard";
import { SITE } from "@/data/content";

const MAX_CARDS = 8;

const formatViews = (n, lang) =>
  new Intl.NumberFormat(lang === "te" ? "te-IN" : "en", { notation: "compact", maximumFractionDigits: 1 }).format(n);

const formatDate = (iso, lang) =>
  new Date(iso).toLocaleDateString(lang === "te" ? "te-IN" : "en-IN", { day: "numeric", month: "short", year: "numeric" });

const Skeleton = () => (
  <div className="animate-pulse overflow-hidden rounded-md border border-line bg-card2" data-testid="interview-skeleton">
    <div className="aspect-video bg-line/40" />
    <div className="space-y-3 p-5">
      <div className="h-2 w-16 rounded bg-line/60" />
      <div className="h-4 w-full rounded bg-line/50" />
      <div className="h-4 w-2/3 rounded bg-line/50" />
    </div>
  </div>
);

export const Interviews = () => {
  const { t, lang } = useLang();
  const { videos, loading } = useYouTube();

  // Live uploads first, then curated videos (de-duplicated), capped to the grid size.
  const live = videos.map((v) => ({
    ...v,
    image: v.thumbnail,
    imageFallback: v.thumbnail_fallback,
    category: t.interviews.liveTag,
    meta: `${formatViews(v.views, lang)} ${t.interviews.views} · ${formatDate(v.published, lang)}`,
  }));
  const liveIds = new Set(live.map((v) => v.id));
  const curated = t.interviews.items.filter((v) => !liveIds.has(v.id)).map((v) => ({ ...v, meta: v.guest }));
  const cards = [...live, ...curated].slice(0, MAX_CARDS);

  return (
    <section id="interviews" data-testid="interviews-section" className="bg-ink py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <SectionHeading
            eyebrow={t.interviews.eyebrow}
            title={t.interviews.title}
            titleItalic={t.interviews.titleItalic}
            description={t.interviews.description}
          />
          <Reveal delay={0.2}>
            <a
              href={SITE.youtube}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="view-all-interviews-link"
              className="group flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-ember transition-colors duration-200 hover:text-emberHover"
            >
              {t.interviews.viewAll}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="interviews-grid">
          {loading
            ? Array.from({ length: 4 }, (_, i) => <Skeleton key={i} />)
            : cards.map((item, i) => (
                <Reveal key={item.id} delay={(i % 4) * 0.08}>
                  <VideoCard item={item} testId={`interview-card-${i}`} meta={item.meta} />
                </Reveal>
              ))}
        </div>
      </div>
    </section>
  );
};
